"use client";

import { Experience } from "@ninetailed/experience.js-react";
import React, { useMemo } from "react";

import { parseCustomExperience } from "@/lib/experiences";

import DynamicImport from "../dynamicImport";
import "./Editorial2Col.css";
import { Editorial2ColProps } from "./type";

export default function Editorial2Col({ entry, typeIndex }: Editorial2ColProps) {
  // Parse experiences from entry.nt_experiences
  const parsedExperiences = React.useMemo(() => parseCustomExperience(entry), [entry]);

  // Component function that receives the current entry (baseline or variant)
  const InnerComponent = React.useCallback(
    (currentEntry?: any) => {
      // Use the passed currentEntry if available, otherwise fall back to the original entry
      const activeEntry = currentEntry || entry;

      // Handle different data structures from Ninetailed
      let finalEntry = activeEntry;
      if (activeEntry?.fields) {
        finalEntry = activeEntry.fields;
      }

      const col1Width =
        finalEntry.variant === "50% - 50%"
          ? "md:w-6/12 order-2 md:order-1"
          : finalEntry.variant === "30% - 60%"
            ? "md:w-[30%]"
            : finalEntry.variant === "60% - 30%"
              ? "md:w-[60%]"
              : "";

      const col2Width =
        finalEntry.variant === "50% - 50%"
          ? "md:w-6/12 order-1 md:order-2"
          : finalEntry.variant === "30% - 60%"
            ? "md:w-[60%]"
            : finalEntry.variant === "60% - 30%"
              ? "md:w-[30%]"
              : "";

      // If the variant is a different component type, render it via DynamicImport
      // Strip nt_experiences to prevent infinite loop
      if (finalEntry.type && finalEntry.type !== "editorial2Col") {
        const { nt_experiences, ...cleanEntry } = finalEntry;
        return <DynamicImport components={[cleanEntry]} typeIndex={typeIndex} />;
      }

      return (
        <div
          className="flex flex-row max-md:flex-col justify-center overflow-hidden"
          style={{ backgroundColor: finalEntry.backgroundColor ?? undefined, color: finalEntry.textColor ?? undefined }}
        >
          <div className={col1Width}>
            <DynamicImport
              components={finalEntry?.column1 ? [finalEntry.column1] : []}
              kpi={finalEntry.column2 && "kpIs" in finalEntry.column2 && finalEntry.kpisOnImageMobileOnly ? finalEntry.column2.kpIs : undefined}
              kpiHiddenOnMobile={finalEntry.kpisOnImageMobileOnly}
              typeIndex={typeIndex}
            />
          </div>
          <div className={col2Width}>
            <DynamicImport
              components={finalEntry?.column2 ? [finalEntry.column2] : []}
              kpi={finalEntry.column1 && "kpIs" in finalEntry.column1 && finalEntry.kpisOnImageMobileOnly ? finalEntry.column1.kpIs : undefined}
              kpiHiddenOnMobile={finalEntry.kpisOnImageMobileOnly}
              typeIndex={typeIndex}
            />
          </div>
        </div>
      );
    },
    [entry, typeIndex],
  );

  // Always wrap with Experience component like HomePageHeroCarousel
  return <Experience {...entry} key={`experience-${entry.id}`} id={entry.id as any} component={InnerComponent} experiences={parsedExperiences} />;
}
